import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Database, Download, Upload, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowLeftRight, Trash2 } from 'lucide-react';
import http from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import GorillaDeskImport from './GorillaDeskImport';
import { toast } from 'sonner';

const GREEN = '#8dd110';

// entity value = backend collection name
const ENTITIES = [
  { v: 'customers', label: 'Customers' },
  { v: 'invoices', label: 'Invoices' },
  { v: 'estimates', label: 'Estimates' },
  { v: 'payments', label: 'Payments' },
  { v: 'expenses', label: 'Expenses' },
  { v: 'bills', label: 'Bills (A/P)' },
  { v: 'vendors', label: 'Vendors' },
  { v: 'products', label: 'Products & Services' },
  { v: 'accounts', label: 'Chart of Accounts' },
  { v: 'transactions', label: 'Transactions (Banking)' },
  { v: 'workorders', label: 'Work Orders / Jobs' },
  { v: 'leads', label: 'Leads' },
  { v: 'contracts', label: 'Contracts' },
  { v: 'inventory', label: 'Inventory' },
  { v: 'pestlibrary', label: 'Pest Library' },
];

// Build CSV columns from the union of keys across rows, id first.
const columnsFor = (rows) => {
  const keys = new Set();
  rows.forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
  const ordered = ['id', ...[...keys].filter((k) => k !== 'id')];
  return ordered.map((k) => ({ key: k, header: k }));
};

export default function DataHub() {
  const [entity, setEntity] = useState('customers');
  const [busy, setBusy] = useState('');
  const [csvText, setCsvText] = useState('');
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const label = ENTITIES.find((e) => e.v === entity)?.label || entity;

  const doClear = async () => {
    if (confirmText !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    setBusy('clear'); setResult(null);
    try {
      const r = (await http.post('/admin/clear-data', { confirm: 'DELETE' })).data;
      toast.success(`Cleared ${r.total} demo records. Ready for your real data.`);
      setConfirmText('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Clear failed');
    } finally { setBusy(''); }
  };

  const doExport = async () => {
    setBusy('export'); setResult(null);
    try {
      const rows = (await http.get(`/${entity}`)).data || [];
      if (!rows.length) { toast.error(`No ${label.toLowerCase()} to export yet.`); return; }
      const csv = toCSV(rows, columnsFor(rows));
      downloadFile(`xpertpests-${entity}.csv`, csv);
      toast.success(`Exported ${rows.length} ${label.toLowerCase()} → open in Excel`);
    } catch { toast.error('Export failed'); }
    finally { setBusy(''); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { setCsvText(await readFileAsText(file)); toast.success(`Loaded ${file.name}`); }
    catch { toast.error('Could not read file'); }
    e.target.value = '';
  };

  const doImport = async () => {
    if (!csvText.trim()) { toast.error('Paste CSV or upload a file first'); return; }
    const rows = parseCSV(csvText);
    if (!rows.length) { toast.error('No rows found in the CSV'); return; }
    setBusy('import'); setResult(null);
    try {
      const r = (await http.post(`/${entity}/import`, rows)).data;
      setResult(r);
      toast.success(`Imported: ${r.inserted} added, ${r.updated} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setBusy(''); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Database className="w-7 h-7" style={{ color: GREEN }} />Import / Export</h1>
        <p className="text-slate-500 mt-1">QuickBooks-style round-trip: export any dataset to Excel, fix it, and import it back in bulk.</p>
      </div>

      <Card className="p-4 border-slate-200 flex flex-wrap items-end gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1">Data set</div>
          <Select value={entity} onValueChange={(v) => { setEntity(v); setResult(null); }}>
            <SelectTrigger className="h-10 w-64"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{ENTITIES.map((e) => <SelectItem key={e.v} value={e.v}>{e.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={doExport} disabled={!!busy} className="gap-1.5 text-white h-10" style={{ backgroundColor: GREEN }}>
          {busy === 'export' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Export {label} to CSV
        </Button>
      </Card>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-5 border-slate-200 space-y-3">
          <div className="flex items-center gap-2 font-bold text-slate-900"><Upload className="w-4 h-4" style={{ color: GREEN }} />Import {label}</div>
          <div className="flex gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
              <FileSpreadsheet className="w-4 h-4" />Upload CSV<input type="file" accept=".csv" className="hidden" onChange={onFile} />
            </label>
            <span className="text-xs text-slate-400 self-center">…or paste CSV below</span>
          </div>
          <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={10}
            placeholder="Paste CSV rows here (first row = column headers, must include an 'id' column to update existing records)…"
            className="w-full text-xs font-mono border border-slate-200 rounded-md p-2" />
          <Button onClick={doImport} disabled={!!busy} className="gap-1.5 text-white" style={{ backgroundColor: GREEN }}>
            {busy === 'import' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Import to {label}
          </Button>

          {result && (
            <Card className="p-4 border-emerald-200 bg-emerald-50/60">
              <div className="flex items-center gap-2 font-bold text-emerald-700"><CheckCircle2 className="w-5 h-5" />Import complete</div>
              <div className="text-sm text-slate-700 mt-1">{result.inserted} added · {result.updated} updated · {result.total} rows processed</div>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-sm text-amber-700">
                  <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{result.errors.length} row error(s):</div>
                  <ul className="list-disc pl-5 text-xs mt-1">{result.errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
            </Card>
          )}
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 font-bold text-slate-900 mb-2"><ArrowLeftRight className="w-4 h-4 text-slate-500" />How the round-trip works</div>
          <ol className="list-decimal pl-4 text-sm text-slate-600 space-y-2">
            <li><b>Export</b> the data set to CSV and open it in Excel or Google Sheets.</li>
            <li><b>Fix / add</b> rows. Keep the <code className="text-[11px] bg-slate-100 px-1 rounded">id</code> column to <b>update</b> those records. Leave <code className="text-[11px] bg-slate-100 px-1 rounded">id</code> blank on a new row to <b>add</b> it.</li>
            <li><b>Import</b> the edited CSV back here — matching rows are updated, new rows are added.</li>
          </ol>
          <div className="mt-3 text-xs text-slate-400 space-y-1">
            <div><Badge variant="outline" className="text-slate-500">Tip</Badge> Export first to get the exact column names.</div>
            <div>First row must be headers. UTF-8. Admins only.</div>
          </div>
        </Card>
      </div>

      <GorillaDeskImport />

      <Card className="p-5 border-slate-200">
        <div className="flex items-center gap-2 font-bold text-slate-900 mb-2"><FileSpreadsheet className="w-4 h-4" style={{ color: GREEN }} />Moving other data in from GorillaDesk by hand</div>
        <ol className="list-decimal pl-4 text-sm text-slate-600 space-y-1.5">
          <li>For <b>invoices</b> and <b>payments</b>, use the panel above instead — it reads GorillaDesk's own column names and reconciles rather than duplicating. These steps are for everything else.</li>
          <li>In <b>GorillaDesk</b>, export your <b>Customers</b> to CSV — usually under <i>Settings → Export</i> or the list’s <i>Export</i> button.</li>
          <li>Open the CSV in Excel and rename the column headers to match ours (Tip: <b>Export</b> our empty “Customers” CSV above to see the exact column names, e.g. <code className="text-[11px] bg-slate-100 px-1 rounded">name</code>, <code className="text-[11px] bg-slate-100 px-1 rounded">email</code>, <code className="text-[11px] bg-slate-100 px-1 rounded">phone</code>, <code className="text-[11px] bg-slate-100 px-1 rounded">fullAddress</code>).</li>
          <li>Leave the <code className="text-[11px] bg-slate-100 px-1 rounded">id</code> column blank so every row is added as new.</li>
          <li>Pick the data set above, paste/upload the CSV, and <b>Import</b>. Repeat for Jobs and Invoices.</li>
        </ol>
        <div className="mt-2 text-xs text-slate-400">Do the <b>Clear demo data</b> step first (below) so you start from a clean slate.</div>
      </Card>

      <Card className="p-5 border-red-200 bg-red-50/40">
        <div className="flex items-center gap-2 font-bold text-red-700 mb-1"><Trash2 className="w-4 h-4" />Clear demo data</div>
        <p className="text-sm text-slate-600">Permanently deletes all sample <b>customers, jobs, invoices, estimates, payments, leads, contracts, and inventory</b>. Keeps your login accounts and reference data (pest library, knowledge base). The demo data will not come back after this. Do this once, right before importing your real GorillaDesk data.</p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder='Type DELETE to confirm'
            className="h-9 w-52 border border-red-200 rounded-md px-3 text-sm" />
          <Button onClick={doClear} disabled={busy === 'clear' || confirmText !== 'DELETE'}
            className="gap-1.5 bg-red-600 hover:bg-red-700 text-white h-9">
            {busy === 'clear' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}Clear all demo data
          </Button>
        </div>
      </Card>
    </div>
  );
}
